import React, {useEffect, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import Loader from "../../../../components/template/loader.jsx";
import DispatchService from "../../../../services/dispatch/dispatch-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {formatCaseCard} from "../utils/dashboard-formatters.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";
import {useDashboardContext} from "../context/dashboard-context.js";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import useDashboardModalState from "../hooks/use-dashboard-modal-state.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";

const DESPACHO_WEB_URL = import.meta.env.VITE_API_DESPACHO_WEB;

const StartDispatchModal = ({payload, onClose, onSuccess, onSupportRequired}) => {
    const navigate = navigateHook();
    const [finalObservation, setFinalObservation] = useState("");
    const {configuration, permissions} = useDashboardContext();
    const [nature, setNature] = useState("");
    const [mileage, setMileage] = useState("");
    const [allowViewCall, setAllowViewCall] = useState(false);
    const [mileageWarning, setMileageWarning] = useState(null);
    const [saveInvalidMileage, setSaveInvalidMileage] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const requested = Boolean(payload);
    const attendance = payload?.attendance;
    const garrison = payload?.garrison;
    const attendanceId = attendance?.attendanceId;
    const garrisonId = garrison?.garrisonId;

    const dispatchByAttendanceQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.dispatchByAttendance(attendanceId),
        enabled: requested && Boolean(attendanceId),
        queryFn: () => DispatchService.getDispatchByAttendanceId(attendanceId),
    });

    const hasExistingDispatch = Boolean(dispatchByAttendanceQuery.data);

    const garrisonDispatchQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.garrisonBySetDispatch(garrisonId),
        enabled: requested && Boolean(garrisonId) && dispatchByAttendanceQuery.isSuccess && !hasExistingDispatch,
        queryFn: () => DispatchService.getGarrisonBySetDispatch(garrisonId),
    });

    const isReady = !dispatchByAttendanceQuery.isFetching && (
        dispatchByAttendanceQuery.isError
        || (
            dispatchByAttendanceQuery.isSuccess
            && !hasExistingDispatch
            && !garrisonDispatchQuery.isFetching
            && (garrisonDispatchQuery.isSuccess || garrisonDispatchQuery.isError)
        )
    );
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    useEffect(() => {
        if (!requested) return;

        setNature(attendance?.nature ?? "");
        setFinalObservation("");
        setMileage("");
        setAllowViewCall(false);
        setMileageWarning(null);
        setSaveInvalidMileage(false);
        setIsSubmitting(false);
    }, [requested, attendance?.nature, attendanceId, garrisonId]);

    useEffect(() => {
        if (!requested || !dispatchByAttendanceQuery.isSuccess || !dispatchByAttendanceQuery.data) return;

        if (dispatchByAttendanceQuery.data?.activity === true) {
            const sameGarrison = Number(dispatchByAttendanceQuery.data?.garrison?.id) === Number(garrisonId);
            projectUtils.swalErrorMessage(
                "Não é possível gerar apoio!",
                sameGarrison ? "A guarnição já está despachada para este atendimento." : "Atividade não pode receber apoio.",
                "Ok",
            );
            closeModal();
            return;
        }

        onSupportRequired({
            ...payload,
            garrisonId,
            supportForGarrisonId: dispatchByAttendanceQuery.data?.garrison?.id,
            supportGarrison: dispatchByAttendanceQuery.data?.garrison,
        });
    }, [closeModal, dispatchByAttendanceQuery.data, dispatchByAttendanceQuery.isSuccess, garrisonId, onSupportRequired, payload, requested]);

    useEffect(() => {
        const result = garrisonDispatchQuery.data;
        const modalGarrison = result?.garrison;
        if (!requested || !modalGarrison?.kmControl) return;

        if (configuration.isSuggestKm && modalGarrison.actualKm !== undefined && modalGarrison.actualKm !== null) {
            setMileage(String(modalGarrison.actualKm));
        }
    }, [configuration.isSuggestKm, garrisonDispatchQuery.data, requested]);

    const natureOptions = useMemo(() => {
        const options = (garrisonDispatchQuery.data?.natures ?? []).map(item => ({
            value: item.name ?? item.alias,
            label: item.alias ?? item.name,
        }));

        if (nature && !options.some(option => option.value === nature)) {
            return [...options, {value: nature, label: nature}];
        }

        return options;
    }, [garrisonDispatchQuery.data?.natures, nature]);

    const modalGarrison = garrisonDispatchQuery.data?.garrison;
    const dispatchToClose = garrisonDispatchQuery.data?.dispatch;
    const kmControl = Boolean(modalGarrison?.kmControl);
    const actualKm = modalGarrison?.actualKm;
    const canShowAllowViewCall = permissions.appAllowViewCall && Boolean(attendance?.ramalIdentify);
    const isLoading = dispatchByAttendanceQuery.isLoading || garrisonDispatchQuery.isLoading;
    const hasError = dispatchByAttendanceQuery.isError || garrisonDispatchQuery.isError;

    const handleMileageChange = ({target}) => {
        setMileage(target.value);
        setMileageWarning(null);
        setSaveInvalidMileage(false);
    };

    const validateMileage = async () => {
        if (!kmControl || mileage === "") return true;

        const result = await DispatchService.checkMileage(garrisonId, mileage);
        if (!result?.invalid || saveInvalidMileage) return true;

        setMileageWarning({
            actualKm: result.actualKm === "" || result.actualKm === null || result.actualKm === undefined ? "0" : result.actualKm,
        });
        return false;
    };

    const handleSubmit = async () => {
        if (!nature) {
            projectUtils.swalErrorMessage("", "Selecione uma natureza.", "Ok");
            return;
        }

        setIsSubmitting(true);

        try {
            const mileageIsValid = await validateMileage();
            if (!mileageIsValid) {
                setIsSubmitting(false);
                return;
            }

            const result = await DispatchService.setDispatch({
                garrisonId,
                attendanceId,
                nature,
                mileage,
                dispatchToCloseId: dispatchToClose?.id ?? null,
                allowViewCall,
                finalObservation
            });

            if (!result || result === "BAD_REQUEST") {
                projectUtils.swalErrorMessage("", "Não foi possível gerar o despacho.", "Ok");
                setIsSubmitting(false);
                return;
            }

            const checkbook = result.numberCheckbook ? ` Talão ${result.numberCheckbook}.` : "";
            onSuccess();
            projectUtils.swalSuccessMessage(
                "success",
                "Sucesso",
                `Despacho gerado com sucesso${checkbook}`,
                "Ok",
                true,
                "Editar",
                ({isDismissed}) => {
                    if (isDismissed) {
                        navigate(`${DESPACHO_WEB_URL}/dispatch/form/${result.id}?redir=despacho/dashboard`, true);
                    }
                },
                false,
                false,
                "#fff",
                {cancelButton: "swal-edit-button"},
            );
        } catch (error) {
            console.error("Erro ao gerar despacho pelo dashboard:", error);
            const message = error.response?.data ?? "Não foi possível gerar o despacho.";
            projectUtils.swalErrorMessage("", message, "Ok");
            setIsSubmitting(false);
        }
    };

    const dispatchToCloseAddress = (() => {
        const displacement = dispatchToClose?.displacement?.[dispatchToClose.displacement.length - 1];
        return [
            displacement?.destiny,
            displacement?.destinyNeighborhood,
            displacement?.destinyStreet,
            displacement?.destinyNumber,
        ].filter(Boolean).join(" - ");
    })();

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open} setOpen={closeModal} title="Gerar despacho" className="w-full max-w-2xl" isCloseClick={false} animated={false}>
            <Modal.Body className="relative max-h-[calc(100vh-180px)] flex flex-col gap-4 overflow-auto">
                {hasError && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        Não foi possível carregar os dados para gerar o despacho.
                    </div>
                )}

                {!hasError && (
                    <>
                        <div>
                            <p className="text-[14px] font-bold text-[#676a6c]">Guarnição</p>
                            <span className="text-sm text-[#555]">{modalGarrison?.name ?? garrison?.garrisonName}</span>
                        </div>

                        <div className="flex flex-col">
                            <p className="text-[14px] font-bold text-[#676a6c]">Dados do atendimento</p>
                            <span className="text-sm text-[#555]">{attendance?.natureText ?? nature}</span>
                            <span className="text-sm text-[#555]">{attendance?.place ? formatCaseCard(attendance.place) : ""}</span>
                        </div>

                        <SelectComponent
                            classNameWrapper="w-full"
                            label="Natureza *"
                            value={nature}
                            options={natureOptions}
                            onChange={(selected) => setNature(selected?.value ?? "")}
                        />

                        {kmControl && (
                            <InputForm
                                id="dashboard-final-mileage"
                                label="Km do veículo"
                                integerOnly
                                type="number"
                                value={mileage}
                                min={1}
                                onChange={handleMileageChange}
                            />
                        )}

                        {canShowAllowViewCall && (
                            <InputCheckbox
                                id="dashboard-allow-view-call"
                                label="Permitir Sentry Despacho visualizar gravação telefônica"
                                checked={allowViewCall}
                                onChange={({target}) => setAllowViewCall(target.checked)}
                            />
                        )}

                        {dispatchToClose && (
                            <>
                                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                    <b>O despacho atual será encerrado.</b>
                                    <div>{dispatchToClose.nature}</div>
                                    {dispatchToCloseAddress && <div>{formatCaseCard(dispatchToCloseAddress)}</div>}
                                </div>

                                <InputTextarea label="Observação final" value={finalObservation}
                                               onChange={({target}) => setFinalObservation(target.value)} height={120}/>
                            </>
                        )}

                        {mileageWarning && (
                            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                <div className="mb-2 text-center">
                                    O km informado é menor que o atual. Deseja continuar?<br/>
                                    Km atual: <b>{mileageWarning.actualKm}</b>
                                </div>
                                <InputCheckbox
                                    id="dashboard-save-invalid-mileage"
                                    label="Sim, continuar mesmo assim"
                                    checked={saveInvalidMileage}
                                    onChange={({target}) => setSaveInvalidMileage(target.checked)}
                                />
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" target="primary" onClick={handleSubmit} loading={isSubmitting} disabled={hasError || isLoading} actionFeedbackType='save'>
                    Salvar
                </Button>
            </Modal.Footer>
            </Modal>
        </>
    );
};

export default StartDispatchModal;